import { h as __h } from "preact";
import { Component } from "preact";
/** @import { ComponentChildren } from "preact"; */

import html from "~/util/html.js";
import { timeOnDate } from "~/component/Time.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./DetailList.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./DetailList.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<DetailListProps>}
 *
 * @typedef {object} DetailListProps
 * @property {unknown} value
 * @property {DetailRenderer} [render] Custom content for an entry. Return `undefined` to use the
 *   default content.
 *
 * @typedef {(key: string, value: unknown) => ComponentChildren | undefined} DetailRenderer
 */
export default class DetailList extends Component {
  render() {
    return content("", this.props.value, this.props.render);
  }
}

/**
 * @typedef {object} DetailTableProps
 * @property {{ key: string; label: ComponentChildren; value: ComponentChildren }[]} rows
 */

/** @param {DetailTableProps} props */
export function DetailTable({
  rows
}) {
  return __h("table", {
    "class": css.table
  }, __h("tbody", {}, rows.map(row => __h("tr", {
    key: row.key
  }, __h("th", {
    scope: "row"
  }, row.label), __h("td", {}, row.value)))));
}

/**
 * @param {string} key
 * @param {unknown} value
 * @param {DetailRenderer} [render]
 * @returns {ComponentChildren}
 */
function content(key, value, render) {
  const custom = key ? render?.(key, value) : undefined;
  if (custom !== undefined) return custom;
  if (Array.isArray(value)) {
    if (!value.length) return __h("span", {
      "class": css.empty
    }, "None");
    if (!value.some(isRecord)) return value.map(literal).join(", ");
    return __h("div", {
      "class": css.stack
    }, value.map((item, i) => __h("div", {
      key: i
    }, content("", item, render))));
  }
  if (isRecord(value)) {
    return __h(DetailTable, {
      rows: Object.entries(value).map(([k, v]) => ({
        key: k,
        label: k,
        value: content(k, v, render)
      }))
    });
  }
  if (value === null || value === undefined) return __h("span", {
    "class": css.empty
  }, "None");
  if (key.endsWith("_at") && typeof value === "string") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return __h("span", {
      title: value
    }, timeOnDate.format(date));
  }
  return literal(value);
}

/** @param {unknown} value */
function literal(value) {
  if (typeof value === "string") return value;
  return JSON.stringify(value) ?? "";
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
export function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}